import React, { useState, useEffect } from 'react';
import { 
  ClipboardList, 
  Package, 
  CheckSquare, 
  Calculator, 
  Users, 
  FileText,
  MapPin
} from 'lucide-react';
import axios from 'axios';
import JumpRecords from './SiteManagement/JumpRecords';
import RopeRecords from './SiteManagement/RopeRecords';
import DailyChecks from './SiteManagement/DailyChecks';
import DailyAccounting from './SiteManagement/DailyAccounting';
import StaffManagement from './SiteManagement/StaffManagement';
import Reports from './SiteManagement/Reports';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SiteManagement = () => {
  const [activeTab, setActiveTab] = useState('jump-records');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/api/locations`);
      setLocations(response.data);
      if (response.data.length > 0) {
        setSelectedLocation(response.data[0]._id);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'jump-records', label: 'Jump Records', icon: ClipboardList },
    { id: 'rope-records', label: 'Rope Records', icon: Package },
    { id: 'daily-checks', label: 'Daily Checks', icon: CheckSquare },
    { id: 'daily-accounting', label: 'Daily Accounting', icon: Calculator },
    { id: 'staff-management', label: 'Staff Management', icon: Users },
    { id: 'reports', label: 'Reports', icon: FileText },
  ];

  const renderTabContent = () => {
    if (!selectedLocation) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-400">Please select a location to continue</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'jump-records':
        return <JumpRecords locationId={selectedLocation} />;
      case 'rope-records':
        return <RopeRecords locationId={selectedLocation} />;
      case 'daily-checks':
        return <DailyChecks locationId={selectedLocation} />;
      case 'daily-accounting':
        return <DailyAccounting locationId={selectedLocation} />;
      case 'staff-management':
        return <StaffManagement locationId={selectedLocation} />;
      case 'reports':
        return <Reports locationId={selectedLocation} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Site Management</h1>
          <p className="text-gray-400">Manage daily operations, records, and staff</p>
        </div>

        {/* Location Selector */}
        <div className="flex items-center gap-3 bg-gray-800 rounded-lg p-3 border border-gray-700">
          <MapPin className="text-yellow-400" size={20} />
          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            className="bg-transparent text-white font-medium focus:outline-none cursor-pointer"
          >
            <option value="">Select Location</option>
            {locations.map((location) => (
              <option key={location._id} value={location._id}>
                {location.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="flex overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-yellow-400 text-black'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <Icon size={18} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default SiteManagement;
